/** Generic postMessage bridge — the integration contract for the Metafore CRM
 *  (and any non-Salesforce host). The CRM embeds the softphone in an iframe and
 *  speaks this tiny protocol:
 *
 *   host → cti:   { type: "cti:clickToDial", number, name?, recordId?, recordUrl? }
 *                 { type: "cti:screenPopResult", reqId, recordId?, recordUrl?, name?, company? }
 *   cti  → host:  { type: "cti:screenPop", reqId, number, direction, interactionId? }
 *                 { type: "cti:callLog", ...CallLog (incl. interactionId?) }
 *                 { type: "cti:callState", state, callId, number?, startedAtMs? }
 *                 { type: "cti:visible", visible }
 *                 { type: "cti:ready" }
 *
 *  Kept intentionally minimal so the CRM side is a few lines of JS. */
import type { Contact } from "../telephony/types";
import type { CallLog, HostBridge, HostHandlers, ScreenPopResult } from "./hostBridge";

export class PostMessageHostBridge implements HostBridge {
  readonly name = "postmessage";
  private handlers!: HostHandlers;
  private pending = new Map<string, (r: ScreenPopResult | null) => void>();
  private seq = 0;
  private listener = (e: MessageEvent) => this.onMessage(e);

  static isPresent(): boolean {
    return typeof window !== "undefined" && window.parent !== window;
  }

  async init(handlers: HostHandlers): Promise<void> {
    this.handlers = handlers;
    window.addEventListener("message", this.listener);
    this.post({ type: "cti:ready" });
  }

  private onMessage(e: MessageEvent): void {
    const d = e.data;
    if (!d || typeof d !== "object" || typeof d.type !== "string") return;
    if (d.type === "cti:clickToDial" && typeof d.number === "string") {
      const contact: Contact = { number: d.number, name: d.name, recordId: d.recordId, recordUrl: d.recordUrl, company: d.company };
      this.handlers.onClickToDial(contact);
    } else if (d.type === "cti:screenPopResult" && typeof d.reqId === "string") {
      const resolve = this.pending.get(d.reqId);
      if (resolve) {
        this.pending.delete(d.reqId);
        resolve(d.recordId || d.name ? { recordId: d.recordId, recordUrl: d.recordUrl, name: d.name, company: d.company } : null);
      }
    }
  }

  screenPop(number: string, direction: "inbound" | "outbound", interactionId?: string): Promise<ScreenPopResult | null> {
    const reqId = `sp-${this.seq++}`;
    return new Promise((resolve) => {
      this.pending.set(reqId, resolve);
      this.post({ type: "cti:screenPop", reqId, number, direction, interactionId });
      // Don't hang the UI if the host never answers.
      window.setTimeout(() => {
        if (this.pending.delete(reqId)) resolve(null);
      }, 4000);
    });
  }

  async saveCallLog(log: CallLog): Promise<void> {
    this.post({ type: "cti:callLog", ...log });
  }

  setSoftphoneVisible(visible: boolean): void {
    this.post({ type: "cti:visible", visible });
  }

  notifyCallState(state: "ringing" | "connected" | "ended", info: { callId: string; number?: string; startedAtMs?: number }): void {
    this.post({ type: "cti:callState", state, ...info });
  }

  dispose(): void {
    window.removeEventListener("message", this.listener);
    this.pending.clear();
  }

  private post(msg: Record<string, unknown>): void {
    window.parent?.postMessage(msg, "*");
  }
}
